import { IFilterOrderByItem } from "angular";
import { TransitionService } from "@uirouter/angularjs";

import { IdMap } from "@gtmhub/util";
import { ItemsTreeOrderField, GoalsTreeOrderField } from "../alignmentLoader";
import { IItem } from "../renderer/models";
import { IGridSortingRule, resetPersistedScreenSettings, getPersistedScreenSettings, setPersistedScreenSettings } from "@gtmhub/shared/components/grid-filtering-bar";
import { expandedGoalsScreenPath } from "@gtmhub/goals/filter-utils";
import { storage } from "@gtmhub/core/storage";

export const expandedGoalsKey = "expandedGoals";
export const expandedSelfIdsKey = "expandedSelfIds";
const LOCATED_ITEM_OVERLAY_COLOR = "#021421";

function getOutdatedExpandedGoalsEntryKey(sessionId: string): string {
  return expandedGoalsKey + sessionId;
}

export function getOutdatedExpandedSelfIdsEntryKey(sessionId: string): string {
  return expandedSelfIdsKey + sessionId;
}

export function resetPersistedExpandedItems(sessionId: string): void {
  const screenPath = expandedGoalsScreenPath(sessionId);

  resetPersistedScreenSettings(screenPath, expandedGoalsKey);
  resetPersistedScreenSettings(screenPath, expandedSelfIdsKey);
  storage.remove(getOutdatedExpandedGoalsEntryKey(sessionId));
  storage.remove(getOutdatedExpandedSelfIdsEntryKey(sessionId));
}

export function getPersistedExpandedGoals(sessionId: string) {
  const screenPath = expandedGoalsScreenPath(sessionId);
  // TODO: delete with https://gtmhub.atlassian.net/browse/GVS-6367
  const outdatedStorageEntryKey = getOutdatedExpandedGoalsEntryKey(sessionId);
  const persistedExpandedGoals = getPersistedScreenSettings(screenPath, expandedGoalsKey);
  const persistedExpandedGoalsInStorage = storage.get<IdMap<boolean>>(outdatedStorageEntryKey);

  if (!persistedExpandedGoals && persistedExpandedGoalsInStorage && Object.values(persistedExpandedGoalsInStorage).length) {
    setPersistedScreenSettings(screenPath, expandedGoalsKey, persistedExpandedGoalsInStorage);
    // once extracted we can delete outdated persisting (can be moved outside if block to remove them in any case)
    storage.remove(outdatedStorageEntryKey);
  }

  return getPersistedScreenSettings(screenPath, expandedGoalsKey) || {};
}

export const getPersistedExpandedSelfIds = (sessionId: string): IdMap<boolean> => {
  const screenPath = expandedGoalsScreenPath(sessionId);
  // TODO: delete with https://gtmhub.atlassian.net/browse/GVS-6367
  const outdatedStorageEntryKey = getOutdatedExpandedSelfIdsEntryKey(sessionId);
  const persistedExpandedSelfIds = getPersistedScreenSettings(screenPath, expandedSelfIdsKey);
  const persistedExpandedSelfIdsInStorage = storage.get<IdMap<boolean>>(outdatedStorageEntryKey) || {};

  if (!persistedExpandedSelfIds && persistedExpandedSelfIdsInStorage && Object.values(persistedExpandedSelfIdsInStorage).length) {
    setPersistedScreenSettings(screenPath, expandedSelfIdsKey, persistedExpandedSelfIdsInStorage);
    // once extracted we can delete outdated persisting (this can be moved outside if block to remove them in any case)
    storage.remove(outdatedStorageEntryKey);
  }

  return getPersistedScreenSettings(screenPath, expandedSelfIdsKey) || {};
};

export const isCircularDependency = (item: IItem, newParent: IItem, itemsIdMap: IdMap<IItem>): boolean => {
  const topParent = getTopLevelParent(newParent, itemsIdMap);
  return (topParent && topParent.id === item.id) || newParent?.parent?.id === item.id;
};

const getTopLevelParent = (item: IItem, itemsIdMap: IdMap<IItem>, foundParent = null): IItem => {
  if (item && item.parent) {
    foundParent = itemsIdMap[item.parent.id];
    return getTopLevelParent(foundParent, itemsIdMap, foundParent);
  }

  return foundParent;
};

export const toItemsTreeOrderField = (field: string): ItemsTreeOrderField => {
  const fieldNamesMap: Record<GoalsTreeOrderField, ItemsTreeOrderField> = {
    name: "title",
    "assignee.name": "sortedOwner.name",
    attainment: "attainment.value",
    "designScore.totalPoints": "designScore.totalPoints",
  };

  return fieldNamesMap[field] || field;
};

export const sortingRulesToOrderStrings = (sortingRules: IGridSortingRule[]): string[] => sortingRules.map((rule) => transformSortingRuleToOrderString(rule));

const transformSortingRuleToOrderString = (sortingRule: IGridSortingRule): string => {
  const prefix: "+" | "-" = sortingRule.reversed ? "-" : "+";

  return `${prefix}${sortingRule.fieldName}`;
};

interface IAlphaNumericOrderByItem extends IFilterOrderByItem {
  value: string | number;
  type: string;
  index: number;
}

export const alphanumericComparator = (a: IAlphaNumericOrderByItem, b: IAlphaNumericOrderByItem): -1 | 0 | 1 => {
  if (a.type === "number" && b.type === "number") {
    if (a.value < b.value) {
      return -1;
    }

    if (a.value > b.value) {
      return 1;
    }

    return 0;
  }

  if (a.type !== "string" || b.type !== "string") {
    return a.index < b.index ? -1 : 1;
  }

  // sensitivity: "base" makes the comparison case INsensitive
  return (<string>a.value).localeCompare(<string>b.value, undefined, { numeric: true, sensitivity: "base" }) as -1 | 0 | 1;
};

function innerElementAbsPosCssText(rect: DOMRect, adjustment: number): string {
  const { top, left, height, width } = rect;

  return `
    position: absolute;
    top: ${top - adjustment}px;
    left: ${left - adjustment}px;
    width: ${width + 2 * adjustment}px;
    height: ${height + 2 * adjustment}px;
  `;
}

function borderFixDivCssText(rect: DOMRect): string {
  const adjustment = 10;

  return `
    ${innerElementAbsPosCssText(rect, adjustment)}
    border: 8px solid ${LOCATED_ITEM_OVERLAY_COLOR};
    border-radius: 15px;
  `;
}

function innerAnimatedDivCssText(rect: DOMRect): string {
  const adjustment = 3;

  return `
    ${innerElementAbsPosCssText(rect, adjustment)}
    animation: located-item-glow;
    animation-duration: 0.6s;
  `;
}

function overlayCssText(rect: DOMRect): string {
  const adjustment = 3;
  const topPx = `${rect.top - adjustment}px`;
  const bottomPx = `${rect.bottom + adjustment}px`;
  const leftPx = `${rect.left - adjustment}px`;
  const rightPx = `${rect.right + adjustment}px`;

  return `
    position: fixed;
    top: 0px;
    bottom: 0px;
    left: 0px;
    right: 0px;
    background: linear-gradient(180deg, ${LOCATED_ITEM_OVERLAY_COLOR} 0px ${topPx}, transparent ${topPx} ${bottomPx}, ${LOCATED_ITEM_OVERLAY_COLOR} ${bottomPx} 100%), linear-gradient(90deg, ${LOCATED_ITEM_OVERLAY_COLOR} 0px ${leftPx}, transparent ${leftPx} ${rightPx}, ${LOCATED_ITEM_OVERLAY_COLOR} ${rightPx} 100%);
    opacity: 0.6;
    z-index: 2000;
  `;
}

export function locatedItemOverlayAnimation(rect: DOMRect, $transitions: TransitionService) {
  let overlay = document.createElement("div");
  overlay.setAttribute("data-test-id", "located-item-overlay");
  overlay.style.cssText = overlayCssText(rect);
  let borderFixDiv = document.createElement("div");
  borderFixDiv.style.cssText = borderFixDivCssText(rect);
  let innerAnimatedDiv = document.createElement("div");
  innerAnimatedDiv.style.cssText = innerAnimatedDivCssText(rect);

  overlay.append(borderFixDiv);
  overlay.append(innerAnimatedDiv);
  document.body.append(overlay);

  const removeOverlay = () => {
    unsubscribe();
    overlay.remove();
    innerAnimatedDiv = null;
    borderFixDiv = null;
    overlay = null;
  };

  const removeOverlayTimeout = setTimeout(removeOverlay, 5000);
  const cancel = () => {
    clearTimeout(removeOverlayTimeout);
    removeOverlay();
  };
  const unsubscribe = $transitions.onStart({}, cancel);
  overlay.onclick = cancel;
}
